import { type ReactNode, Suspense, lazy } from 'react';
import { isMockAuthEnabled } from './mockAuth';
import { FullPageLoader } from '@/shared/ui';

/**
 * Async chunk: loads `@azure/msal-react` + `@azure/msal-browser` only when rendered.
 * In mock-auth mode this component is never mounted, so vendor-msal stays unloaded.
 */
const MsalLayer = lazy(async () => {
  const [{ MsalProvider }, instance] = await Promise.all([
    import('@azure/msal-react'),
    import('./msal').then((m) => m.getMsalInstance()),
  ]);
  const Layer = ({ children }: { children: ReactNode }) => (
    <MsalProvider instance={instance}>{children}</MsalProvider>
  );
  return { default: Layer };
});

type Props = { children: ReactNode };

/**
 * Wraps children with `MsalProvider` (lazy-loaded).
 * In mock-auth mode the MSAL layer is skipped entirely — children render immediately
 * without loading vendor-msal (~480 kB).
 */
export function LazyMsalProvider({ children }: Props) {
  if (isMockAuthEnabled()) {
    return <>{children}</>;
  }
  return (
    // This boundary wraps `RouterProvider`, so a `null` fallback blanks the
    // whole app — including the loaders in `index.tsx` / `App.tsx`.
    <Suspense fallback={<FullPageLoader />}>
      <MsalLayer>{children}</MsalLayer>
    </Suspense>
  );
}
